<script setup lang="ts">
import { useLocalStorage } from '@vueuse/core'

import type { SwordDrillMode } from '~/stores/swordDrill'

const props = withDefaults(defineProps<{
  pairingCode?: string
  pairingUrl?: string
  peerPresentersCount?: number
  peerReady?: boolean
  popupPresenterConnected?: boolean
  presentationConnecting?: boolean
  presentationSupported?: boolean
  presenterEntryUrl?: string
}>(), {
  pairingCode: '',
  pairingUrl: '',
  peerPresentersCount: 0,
  peerReady: false,
  popupPresenterConnected: false,
  presentationConnecting: false,
  presentationSupported: true,
  presenterEntryUrl: '',
})

const emit = defineEmits<{
  connectPresentationWindow: []
  disconnectPeerPresenters: []
  disconnectPresentation: []
  launchDisplayMode: []
  regeneratePairingCode: []
  start: []
}>()

const store = useSwordDrillStore()
const { data: ui } = useSwordDrillUI()

// ── Persisted setup option ────────────────────────────
const mode = useLocalStorage<SwordDrillMode>('sfg.swordDrill.mode', 'full-bible')

const modes = computed(() => ui.value?.setup.mode.options ?? [])

// ── Reset ──────────────────────────────────────────────
function handleReset() {
  mode.value = 'full-bible'
}

defineExpose({ handleReset })

// ── Start ──────────────────────────────────────────────
function handleStart() {
  store.config.mode = mode.value
  emit('start')
}
</script>

<template>
  <div class="space-y-8">
    <!-- ── Mode ───────────────────────────────────────────── -->
    <UCard :ui="{ header: 'bg-muted/50', body: 'bg-muted/10' }">
      <template #header>
        <div class="flex items-center gap-3">
          <UIcon
            class="text-primary size-9 shrink-0"
            name="mdi:bookshelf"
          />
          <div>
            <h2 class="text-lg font-semibold">
              {{ ui?.setup.mode.title }}
            </h2>
            <p class="text-sm text-muted">
              {{ ui?.setup.mode.description }}
            </p>
          </div>
        </div>
      </template>
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <SfgOptionCard
          v-for="m in modes"
          :key="m.value"
          :selected="mode === m.value"
          @click="mode = m.value"
        >
          <div class="flex items-start gap-3 px-4 py-3">
            <UIcon
              :name="m.icon"
              class="size-7 shrink-0 mt-0.5"
            />
            <div class="min-w-0">
              <div class="font-semibold">
                {{ m.label }}
              </div>
              <div class="mt-0.5 text-xs text-brand-ink/70">
                {{ m.description }}
              </div>
            </div>
          </div>
        </SfgOptionCard>
      </div>
    </UCard>

    <!-- ── Presentation Screen ──────────────────────────── -->
    <GamePresentationCard
      :pairing-code="props.pairingCode"
      :pairing-url="props.pairingUrl"
      :peer-presenters-count="props.peerPresentersCount"
      :peer-ready="props.peerReady"
      :popup-presenter-connected="props.popupPresenterConnected"
      :presentation-connecting="props.presentationConnecting"
      :presentation-supported="props.presentationSupported"
      :presenter-entry-url="props.presenterEntryUrl"
      @connect-presentation-window="emit('connectPresentationWindow')"
      @disconnect-peer-presenters="emit('disconnectPeerPresenters')"
      @disconnect-presentation="emit('disconnectPresentation')"
      @launch-display-mode="emit('launchDisplayMode')"
      @regenerate-pairing-code="emit('regeneratePairingCode')"
    />

    <!-- ── Start Button ──────────────────────────────────── -->
    <div class="flex flex-col items-center gap-3 pt-2">
      <UButton
        icon="mdi:play-circle"
        :label="ui?.setup.startButton"
        size="xl"
        @click="handleStart"
      />
    </div>
  </div>
</template>
